import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, Subscription } from "../../lib/api";
import { money, shortDate } from "../../lib/format";
import { StatusBadge } from "../StatusBadge";

interface ClubSummary { tiers: { id: string; name: string; price_pence: number }[]; membership: { tier: string; funding: string; status: string; next_case_due: string; custom_price_pence?: number } | null }

const cadence = (s: Subscription) => `every ${s.interval > 1 ? `${s.interval} ${s.interval_unit.replace("ly", "s")}` : s.interval_unit.replace("ly", "")}`;

export function PlanSummary() {
  const { data: subs } = useQuery<Subscription[]>({ queryKey: ["subscriptions"], queryFn: () => api.get("/subscriptions").then((r) => r.data) });
  const { data: club } = useQuery<ClubSummary>({ queryKey: ["club"], queryFn: () => api.get("/club").then((r) => r.data) });
  const cellar = subs?.find((s) => (s.status === "active" || s.status === "paused") && !s.club);
  const m = club?.membership && club.membership.status !== "cancelled" ? club.membership : null;
  const tier = club?.tiers.find((t) => t.id === m?.tier);

  return (
    <div data-testid="plan-summary">
      <div className="eyebrow">Your current plans</div>
      <div className="mt-4 grid gap-px border border-border sm:grid-cols-2">
        <div className="bg-[var(--parchment)] p-6 sm:border-r sm:border-border" data-testid="plan-summary-cellar">
          <div className="eyebrow">Cellar plan · Direct Debit</div>
          {subs === undefined ? <div className="mt-3 font-serif text-3xl text-neutral-400">—</div> : cellar ? (
            <>
              <div className="mt-3 font-serif text-3xl tabular-nums">{money(cellar.amount_pence)} <span className="text-base text-neutral-600">{cadence(cellar)}</span></div>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <StatusBadge status={cellar.status} testId="plan-summary-cellar-status" />
                {cellar.next_charge_date && <span>Next collection {shortDate(cellar.next_charge_date)}</span>}
              </div>
            </>
          ) : (
            <>
              <div className="mt-3 font-serif text-3xl text-neutral-400" data-testid="plan-summary-cellar-none">No plan yet</div>
              <p className="mt-3 text-sm text-neutral-600">Start one below to build your account balance automatically.</p>
            </>
          )}
        </div>
        <div className="bg-[var(--parchment)] p-6" data-testid="plan-summary-club">
          <div className="eyebrow">Wine Club plan</div>
          {club === undefined ? <div className="mt-3 font-serif text-3xl text-neutral-400">—</div> : m ? (
            <>
              <div className="mt-3 font-serif text-3xl">{tier?.name || "Bespoke plan"}</div>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <StatusBadge status={m.status} testId="plan-summary-club-status" />
                <span>{money(tier?.price_pence ?? m.custom_price_pence ?? 0)} per case · funded {m.funding.replace("_", " ")}</span>
                {m.next_case_due && <span>Next case {shortDate(m.next_case_due)}</span>}
              </div>
              <Link to="/club" className="mt-3 inline-block text-xs underline underline-offset-4" data-testid="plan-summary-club-link">Manage Wine Club →</Link>
            </>
          ) : (
            <>
              <div className="mt-3 font-serif text-3xl text-neutral-400" data-testid="plan-summary-club-none">No plan yet</div>
              <p className="mt-3 text-sm text-neutral-600">Twelve bottles chosen for you, every twelve weeks. <Link to="/club" className="underline underline-offset-4" data-testid="plan-summary-club-join">See the plans →</Link></p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
